import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { designerItemsAPI } from '../../api/designerItemsAPI';
import { boutiqueItemsAPI } from '../../api/boutiqueItemsAPI';
import { artistItemsAPI } from '../../api/artistItemsAPI';
import { navigateRequireAuth } from '../../utils/authRedirect';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { normalizeShopApprovalStatus } from '../../utils/shopOwnerPortal';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; }`;

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 1rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 560px;
  background: white;
  border-radius: 1.25rem;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
  padding: 2.25rem 2rem;
  animation: ${fadeIn} 0.4s ease;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0 0 0.5rem;
  color: #1e293b;
`;

const Body = styled.p`
  color: #475569;
  line-height: 1.6;
  margin: 0 0 1.25rem;
`;

const StatusPill = styled.div`
  display: inline-block;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: ${p => (p.$status === 'APPROVED' ? '#d1fae5' : p.$status === 'REJECTED' ? '#fee2e2' : '#fef3c7')};
  color: ${p => (p.$status === 'APPROVED' ? '#065f46' : p.$status === 'REJECTED' ? '#991b1b' : '#92400e')};
`;

const HomeLink = styled(Link)`
  display: inline-block;
  margin-top: 0.5rem;
  color: #764ba2;
  font-weight: 700;
  text-decoration: none;
`;

const ReapplyButton = styled(Link)`
  display: inline-block;
  margin: 0.25rem 0 0.75rem;
  padding: 0.7rem 1.25rem;
  border-radius: 0.75rem;
  background: #764ba2;
  color: #fff;
  font-weight: 700;
  text-decoration: none;
`;

const shopTypeConfig = {
  designer: {
    label: 'shop',
    api: designerItemsAPI,
    browsePath: '/designers',
    createTo: '/create-shop',
    publicPath: (shop) => `/designer-shop/${designerItemsAPI.buildShopRef(shop)}`,
  },
  boutique: {
    label: 'boutique',
    api: boutiqueItemsAPI,
    browsePath: '/boutiques',
    createTo: '/create-boutique',
    publicPath: (shop) => `/boutique-shop/${boutiqueItemsAPI.buildShopRef(shop)}`,
  },
  artist: {
    label: 'gallery',
    api: artistItemsAPI,
    browsePath: '/artists',
    createTo: { pathname: '/create-shop', search: '?kind=artists' },
    publicPath: (shop) => `/artist-shop/${artistItemsAPI.buildShopRef(shop)}`,
  },
};

const ShopPendingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useSelector((s) => s.auth);
  const rawType = (searchParams.get('type') || 'designer').toLowerCase();
  const shopType = shopTypeConfig[rawType] ? rawType : 'designer';
  const config = shopTypeConfig[shopType];
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadShop = useCallback(async ({ silent = false } = {}) => {
    if (!isAuthenticated) {
      navigateRequireAuth(navigate, { pathname: '/shop-pending', search: `?type=${shopType}` });
      return;
    }
    try {
      const data = await config.api.getMyShop({ compact: true });
      setShop(data);
      const approval = normalizeShopApprovalStatus(data?.approval_status);
      if (approval === 'APPROVED' && data?.id) {
        navigate(config.publicPath(data), { replace: true });
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        if (!silent) toast.error('Your session expired. Please sign in again.');
        navigateRequireAuth(navigate, { pathname: '/shop-pending', search: `?type=${shopType}` });
        return;
      }
      if (status === 404) {
        navigate(
          shopType === 'boutique'
            ? '/create-boutique'
            : `/create-shop${shopType === 'artist' ? '?kind=artists' : ''}`,
          { replace: true },
        );
        return;
      }
      if (!silent) toast.error('Failed to load shop status.');
    } finally {
      setLoading(false);
    }
  }, [config, isAuthenticated, navigate, shopType]);

  useEffect(() => {
    loadShop();
  }, [loadShop]);

  const status = normalizeShopApprovalStatus(shop?.approval_status) || (loading ? '' : 'PENDING');
  const shopId = shop?.id;

  useEffect(() => {
    if (status === 'APPROVED' || status === 'REJECTED' || !shopId) return undefined;
    const timer = setInterval(() => {
      loadShop({ silent: true });
    }, 2500);
    return () => clearInterval(timer);
  }, [loadShop, shopId, status]);

  if (loading && !shop) return <LoadingSpinner text="Loading shop status…" />;

  const noun = config.label;

  return (
    <Page>
      <Card>
        <StatusPill $status={status}>
          {status === 'APPROVED' ? 'Approved' : status === 'REJECTED' ? 'Not approved' : 'Pending review'}
        </StatusPill>
        <Title>
          {status === 'REJECTED' ? `Your ${noun} was not approved` : `Your ${noun} is under review`}
        </Title>
        <Body>
          {status === 'REJECTED'
            ? (shop?.rejection_note || 'An admin left a note with the decision. You can update your details and submit again.')
            : `Thanks for submitting ${shop?.brand_name ? `"${shop.brand_name}"` : `your ${noun}`}. It is only visible to you until an admin approves it. Catalog items are reviewed separately.`}
        </Body>
        {status === 'REJECTED' ? (
          <ReapplyButton to={config.createTo} state={{ reapply: true }} replace>
            Re-apply
          </ReapplyButton>
        ) : null}
        <HomeLink to={config.browsePath}>Back to {shopType === 'artist' ? 'Artists' : shopType === 'boutique' ? 'Boutiques' : 'Designers'}</HomeLink>
      </Card>
    </Page>
  );
};

export default ShopPendingPage;
